import type { ReactNode } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import WarningIcon from '@mui/icons-material/Warning';
import InventoryIcon from '@mui/icons-material/Inventory';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BarChartIcon from '@mui/icons-material/BarChart';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import QueryStatsIcon from '@mui/icons-material/QueryStats';

import { useGetRemindersSummaryQuery } from '#services/order';

interface QuickAction {
  title: string;
  description: string;
  icon: ReactNode;
  link: string;
  badge?: number | string;
}

export default function AdminHome() {
  const navigate = useNavigate();
  const currentUser = useSelector(
    (state: { auth: { userInfo: { isAdmin?: boolean; isStaff?: boolean } } }) => state.auth.userInfo,
  );
  const { data: remindersSummary } = useGetRemindersSummaryQuery(undefined, {
    skip: !currentUser.isAdmin && !currentUser.isStaff,
  });

  const reminderCount = (remindersSummary as { total?: number } | undefined)?.total || 0;

  if (!currentUser.isAdmin) {
    return (
      <Typography variant="body1" sx={{ mb: 2 }}>
        This is the dashboard for admin/staff. More functions are under construction.
      </Typography>
    );
  }

  const quickActions: QuickAction[] = [
    {
      title: 'View Customer Orders',
      description: 'Browse and manage all customer orders',
      icon: <ListAltIcon />,
      link: '/management/order/list',
    },
    {
      title: 'Create Customer Orders',
      description: 'Shop and create orders for customers',
      icon: <ShoppingCartIcon />,
      link: '/shop',
    },
    {
      title: 'View Overdue Orders',
      description: 'Check orders that need attention',
      icon: <WarningIcon />,
      link: '/management/order/overdue',
    },
    {
      title: 'Order Reminders',
      description: `${reminderCount} active ${reminderCount === 1 ? 'reminder' : 'reminders'}`,
      icon: <NotificationsActiveIcon />,
      link: '/management/order/reminders',
      badge: reminderCount,
    },
    {
      title: 'Create Inventory Orders',
      description: 'Shop and create orders for inventory',
      icon: <InventoryIcon />,
      link: '/shop?inventory=true',
    },
    {
      title: 'View Inventory Report',
      description: 'Check current inventory status',
      icon: <AssessmentIcon />,
      link: '/management/inventory/report',
    },
    {
      title: 'Analytics Dashboard',
      description: 'Detailed sales metrics and insights',
      icon: <BarChartIcon />,
      link: '/management/analytics/dashboard',
    },
    {
      title: 'AI Reports',
      description: 'AI-generated warehouse insights and summaries',
      icon: <AutoAwesomeIcon />,
      link: '/management/analytics/ai-reports',
      badge: 'exp',
    },
    {
      title: 'Inventory Analytics',
      description: 'Forecasting, sell-through rates, and stock status',
      icon: <QueryStatsIcon />,
      link: '/management/inventory/analytics',
      badge: 'exp',
    },
  ];

  return (
    <>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Welcome to the Admin Dashboard
      </Typography>
      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Quick actions to help you navigate the system efficiently
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 2,
        }}
      >
        {quickActions.map((action) => (
          <Card
            key={action.title}
            variant="outlined"
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              border: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: 1,
              },
            }}
            onClick={() => { void navigate(action.link); }}
          >
            {typeof action.badge === 'number' && action.badge > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  borderRadius: '50%',
                  minWidth: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  px: 0.5,
                }}
              >
                {action.badge}
              </Box>
            )}
            {typeof action.badge === 'string' && (
              <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                <Chip
                  label={action.badge}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5, py: 0 } }}
                />
              </Box>
            )}
            <CardContent
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 2,
                p: 2,
              }}
            >
              <Box
                sx={{
                  minWidth: 48,
                  minHeight: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                  backgroundColor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                {action.icon}
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" component="div" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {action.title}
                </Typography>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  {action.description}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </>
  );
}
